import React, {useContext, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {EventCreateStampContext} from '~/common/fabric/CreateDesign/components/Stamp/EventCreateStampContext';
import {fabricGif} from '~/common/canvas/animation/gif/Gif';
import {selectStickers} from '~/CreateFabricGreetingCard/reducers/fabricTemplate/selectors';
import {IconUnit} from '~/common/fabric/Card/components/IconUnit/IconUnit';
import {UPDATE_DESIGN_OPTIONS} from '~/CreateFabricGreetingCard/reducers/fabricDesign/constants';
import {useMatchQuery} from '~library/hooks/useMatchQuery';
import {largishAndUp} from '~libSass/base/_exports.sass';
import './Stickers.sass';

const ELEMENT_WIDTH = 200;

export const Stickers = () => {
  const {canvas} = useContext(EventCreateStampContext);
  const stickers = useSelector(selectStickers);
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(false);
  const {matches: isDesktop} = useMatchQuery(`(min-width: ${largishAndUp})`);
  const isMobile = !isDesktop;

  useEffect(
    () => () => {
      if (canvas?.getActiveObject()) {
        canvas?.discardActiveObject()?.requestRenderAll();
      }
    },
    [canvas]
  );

  useEffect(() => {
    const onSelectionEvent = () => {
      if (isMobile) {
        dispatch({
          type: UPDATE_DESIGN_OPTIONS,
          payload: {editingMobile: true},
        });
      }
    };

    canvas?.on('selection:created', onSelectionEvent);
    canvas?.on('selection:updated', onSelectionEvent);

    return () => {
      canvas?.off('selection:created', onSelectionEvent);
      canvas?.off('selection:updated', onSelectionEvent);
    };
  }, [canvas, isMobile]);

  const filterIconImage = (type) =>
    stickers?.reduce((previousValue, currentValue) => {
      const url =
        currentValue.media.find((media) => media.label === type).url ||
        currentValue.media.find((media) => media.label === 'image').url || // fallback if the icon doesn't exist
        currentValue.media.find((media) => media.label === 'icon').url; // fallback if the image doesn't exist

      const sticker = {
        id: currentValue.id,
        url,
      };
      return [sticker, ...previousValue];
    }, []);

  const stickersIcons = filterIconImage('icon');
  const stickersImages = filterIconImage('image');
  const imagesMap = stickersImages.reduce(
    (previousValue, currentValue) => ({
      ...previousValue,
      [currentValue.id]: currentValue.url,
    }),
    {}
  );

  const addElementToCanvas = (obj, isGif) => {
    obj.set({
      top: isDesktop ? obj.top + 50 : obj.top + 100,
      left: obj.left + 50,
    });

    canvas.add(obj);

    if (isGif) {
      window.fabric.util.requestAnimFrame(function render() {
        canvas.renderAll();
        window.fabric.util.requestAnimFrame(render);
      });
    }
  };

  const onClickStickerHandler = async (gif, id) => {
    setDisabled(true);
    const image = imagesMap[id];
    const isGif = image.split('.').pop() === 'gif';

    if (isGif) {
      const g = await fabricGif(image, {originalUrl: image}, ELEMENT_WIDTH);
      setDisabled(false);
      return addElementToCanvas(g, isGif);
    }

    // eslint-disable-next-line new-cap
    return new window.fabric.Image.fromURL(image, (myImg) => {
      myImg.scaleToWidth(ELEMENT_WIDTH);
      addElementToCanvas(myImg, isGif);
      setDisabled(false);
    });
  };

  const iconsMemo = useMemo(
    () =>
      stickersIcons?.map((icon) => (
        <IconUnit
          key={icon.id}
          icon={icon.url}
          id={icon.id}
          onClick={onClickStickerHandler}
          square
          height={40}
          width={46}
          disabled={disabled}
        />
      )),
    [stickersIcons]
  );

  return <div className={isDesktop ? 'stickers' : 'icons--mobile'}>{iconsMemo}</div>;
};
